import React from 'react';
import { UpcomingEvents } from '../components/UpcomingEvents';
import { Calendar, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { SEO_CONTENT } from '../data/seoContent';

const EVENTS = [
  {
    id: 'imid-2026',
    title: 'IMID 2026, Booth 21',
    date: 'August 18 to 21, 2026',
    location: 'BEXCO, Busan, Republic of Korea',
    desc: 'LUMORA exhibited at the 26th International Meeting on Information Display with our OLED and MR-TADF emitter portfolio, high-purity hosts and transport materials. Read the LG Display and Samsung Display technology highlights from the show.',
    link: '/research-highlights/imid-2026-booth-21.html',
    featured: '/imid-2026-booth-21.jpg',
    featuredAlt: 'LUMORA at IMID 2026, Booth 21, BEXCO Busan',
    images: [
      { src: '/research-highlights/images/imid-2026-booth-21.jpg', alt: 'IMID 2026 exhibition' }
    ]
  },
  {
    id: 'k-display-2026',
    title: 'K-Display 2026',
    date: 'July 22 to 24, 2026',
    location: 'COEX, Seoul, Republic of Korea',
    desc: 'Our materials view of the K-Display 2026 showcases: LG Display fourth generation tandem OLED, 540 Hz gaming panels and LTPO, and Samsung Display Privacy OLED, QD-OLED and the digital cockpit.',
    link: '/research-highlights/lg-display-k-display-2026.html',
    featured: '/research-highlights/images/lg-display-k-display-2026.jpg',
    featuredAlt: 'K-Display 2026 at COEX, Seoul',
    images: [
      { src: '/research-highlights/images/lg-kd-01.jpg', alt: 'K-Display 2026 exhibition' },
      { src: '/research-highlights/images/sd-kd-01.jpg', alt: 'K-Display 2026 exhibition' },
      { src: '/research-highlights/images/lg-kd-02.jpg', alt: 'K-Display 2026 exhibition' },
      { src: '/research-highlights/images/sd-kd-02.jpg', alt: 'K-Display 2026 exhibition' }
    ]
  },
  {
    id: 'deeptech-2026',
    titleKey: 'events.deeptech2026Title',
    date: '26-27 March 2026',
    locationKey: 'events.deeptech2026Location',
    descKey: 'events.deeptech2026Desc',
    featured: '/images/deeptech2026/deeptech-group.jpg',
    featuredAlt: 'Group photo at International Industry-Academic DeepTech Conference 2026',
    images: [
      { src: '/images/deeptech2026/deeptech-presentation.jpeg', alt: 'LUMORA presentation on Next-Generation OLED Materials' },
      { src: '/images/deeptech2026/deeptech-certificate.jpeg', alt: 'Certificate presentation at DeepTech Conference 2026' },
      { src: '/images/deeptech2026/deeptech-speaker.jpeg', alt: 'Dr. Yogesh Gawale speaking at DeepTech Conference' },
      { src: '/images/deeptech2026/deeptech-speaking.jpeg', alt: 'Keynote presentation at DeepTech Conference 2026' }
    ]
  },
  {
    id: 'imid-2025',
    titleKey: 'events.imid2025Title',
    date: 'August 2025',
    locationKey: 'events.imid2025Location',
    descKey: 'events.imid2025Desc',
    featured: '/images/imid2025/imid-lumora-booth.png',
    featuredAlt: 'LUMORA booth at IMID 2025',
    images: [
      { src: '/images/imid2025/imid-group-banquet.jpeg', alt: 'Group photo at IMID 2025 banquet' },
      { src: '/images/imid2025/imid-booth-meeting.jpeg', alt: 'Meeting at LUMORA exhibition booth' },
      { src: '/images/imid2025/imid-selfie-group.jpeg', alt: 'Team selfie at IMID 2025' },
      { src: '/images/imid2025/imid-selfie-banquet.jpeg', alt: 'Networking at IMID 2025 banquet' }
    ]
  }
];

export const Events = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen pt-20">
      <SEO {...SEO_CONTENT.events} />
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            {t('events.title')}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            {t('events.subtitle')}
          </p>
        </div>
      </section>

      <UpcomingEvents compact />

      {/* Timeline */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Vertical line */}
            <div className="hidden md:block absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-500 via-teal-500 to-gray-300"></div>

            <div className="space-y-16 md:space-y-24">
              {EVENTS.map((event, idx) => (
                <div key={event.id} className="relative" data-testid={`event-${event.id}`}>
                  {/* Timeline dot */}
                  <div className="hidden md:flex absolute left-8 -translate-x-1/2 w-5 h-5 rounded-full border-4 border-white shadow-md z-10"
                    style={{ backgroundColor: idx === 0 ? '#ea580c' : '#0d9488' }}
                  ></div>

                  <div className="md:ml-20">
                    {/* Date & Location badge */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-900 text-white text-sm font-semibold">
                        <Calendar size={14} />
                        {event.date}
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                        <MapPin size={14} />
                        {event.location || t(event.locationKey)}
                      </span>
                    </div>

                    {/* Title & Description */}
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                      {event.title || t(event.titleKey)}
                    </h2>
                    <p className="text-base md:text-lg text-gray-600 mb-8 max-w-3xl">
                      {event.desc || t(event.descKey)}
                    </p>

                    {event.link && (
                      <a href={event.link} className="inline-block mb-6 text-orange-600 font-semibold hover:text-orange-700">Read the highlight &rarr;</a>
                    )}
                    {/* Featured image */}
                    <div className="mb-6 overflow-hidden rounded-xl shadow-lg group">
                      <img
                        src={event.featured}
                        alt={event.featuredAlt}
                        className="w-full h-64 sm:h-80 md:h-96 object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>

                    {/* Gallery grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                      {event.images.map((img, i) => (
                        <div key={i} className="overflow-hidden rounded-lg shadow-md group aspect-square">
                          <img
                            src={img.src}
                            alt={img.alt}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
